import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X } from "lucide-react";
import api from "../lib/api";

export const SearchDropdown = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      if (products.length === 0) {
        api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
      }
      setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [open, products.length]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const results = query
    ? products.filter(
        (p) =>
          p.name.toLowerCase().includes(query.toLowerCase()) ||
          p.category.toLowerCase().includes(query.toLowerCase())
      )
    : products;

  const go = (id) => {
    setOpen(false);
    setQuery("");
    navigate(`/product/${id}`);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 border border-[#2C2623] hover:border-[#E05A36] px-4 py-2 transition-colors"
        data-testid="search-toggle-button"
        aria-label="Search products"
      >
        <Search className="w-4 h-4" />
      </button>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-[#0C0A09]/60 backdrop-blur-sm z-40"
              data-testid="search-overlay"
            />
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="fixed left-0 right-0 top-[100px] z-50 bg-[#171412] border-b border-[#2C2623] shadow-[0_30px_60px_rgba(0,0,0,0.5)]"
              data-testid="search-dropdown"
            >
              <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6">
                <div className="flex items-center gap-3 border border-[#2C2623] focus-within:border-[#E05A36] bg-[#0C0A09] px-4 transition-colors">
                  <Search className="w-4 h-4 text-[#7A7067] shrink-0" />
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Quick search — tees, hoodies, caps..."
                    className="flex-1 bg-transparent outline-none py-3.5 text-sm"
                    data-testid="header-search-input"
                  />
                  <button onClick={() => setOpen(false)} className="text-[#7A7067] hover:text-[#E05A36] transition-colors" data-testid="search-close-button">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="max-h-[50vh] overflow-y-auto mt-2 divide-y divide-[#2C2623]" data-testid="search-results">
                  {results.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => go(p.id)}
                      className="w-full flex items-center gap-4 py-3 px-2 hover:bg-[#1E1A17] transition-colors text-left"
                      data-testid={`search-result-${p.id}`}
                    >
                      <img src={p.image} alt="" className="w-11 h-14 object-cover shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold truncate">{p.name}</p>
                        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#7A7067]">{p.category}</p>
                      </div>
                      <span className="font-mono text-sm text-[#E29D47] shrink-0">${p.price.toFixed(2)}</span>
                    </button>
                  ))}
                  {results.length === 0 && (
                    <p className="py-8 text-center text-xs font-mono uppercase tracking-widest text-[#7A7067]" data-testid="search-no-results">
                      No pieces match "{query}"
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
